/**
 * Maps human-readable keys to translation keys in the "insurance" namespace.
 * Used by the insurance page to look up translations from messages/{locale}.json.
 *
 * Most of the insurance page content was exported from Framer into the "careers"
 * namespace. We duplicate the values under human-readable keys in the "insurance"
 * namespace so the page has a clean, self-contained translation layer.
 */
export const insuranceKeys = {
  // Hero
  heroLabel: "heroLabel",
  heroHeading: "heroHeading",
  heroSubtitle: "heroSubtitle",
  checkCoverageButton: "checkCoverageButton",

  // How it works
  howItWorksHeading: "howItWorksHeading",
  step1Title: "step1Title",
  step1Description: "step1Description",
  step2Title: "step2Title",
  step2Description: "step2Description",
  step3Title: "step3Title",
  step3Description: "step3Description",
  step4Title: "step4Title",
  step4Description: "step4Description",

  // Coverage form
  formHeading: "formHeading",
  formInsurerLabel: "formInsurerLabel",
  formInsurerPlaceholder: "formInsurerPlaceholder",
  formCheckupLabel: "formCheckupLabel",
  formCheckupPlaceholder: "formCheckupPlaceholder",
  formEstimatedCoverage: "formEstimatedCoverage",
  formCoverageVaries: "formCoverageVaries",
  talkToTeam: "talkToTeam",

  // Insurer names (form options)
  insurerOther: "insurerOther",

  // Checkup options
  checkupMriCore: "checkupMriCore",
  checkupMriAdvanced: "checkupMriAdvanced",
  checkupMriPro: "checkupMriPro",
  checkupBlood: "checkupBlood",

  // Insurance types
  //
  // Same two-variant split as the insurer cards below, for the same reason:
  //
  //   insuranceTypes* / basicInsurance* / supplementaryInsurance*
  //     — the shared education copy, rendered by the `/go/insurancemri` and
  //       `/go/insuranceblood` ad LPs.
  //   *Coverage*
  //     — the /insurance wording, rendered only by `insurance-client.tsx` and
  //       its markdown mirror (`insuranceMarkdown`).
  //
  // The FR catalog is the only one where the two diverge today: the 2026-08-18
  // Swiss-French rewrite retitled the cards LAMal/LCA and named the market in
  // the heading, and that was scoped to /assurance. EN and DE repeat the shared
  // wording so /ch/en and /ch/de render exactly as before. Keep it that way —
  // editing the shared key to "fix" the duplication silently rewrites both LPs.
  insuranceTypesHeading: "insuranceTypesHeading",
  insuranceTypesCoverageHeading: "insuranceTypesCoverageHeading",
  basicInsuranceTitle: "basicInsuranceTitle",
  basicInsuranceCoverageTitle: "basicInsuranceCoverageTitle",
  basicInsuranceDescription: "basicInsuranceDescription",
  basicInsuranceCoverageDescription: "basicInsuranceCoverageDescription",
  supplementaryInsuranceTitle: "supplementaryInsuranceTitle",
  supplementaryInsuranceCoverageTitle: "supplementaryInsuranceCoverageTitle",
  supplementaryInsuranceDescription: "supplementaryInsuranceDescription",
  // The pill over the supplementary photo on the two ad LPs. Same story as
  // the /insurance plates below: it sat hardcoded in both clients, so the
  // French LPs showed a German law name. Shared key, so it mirrors.
  supplementaryInsuranceBadge: "supplementaryInsuranceBadge",
  supplementaryInsuranceCoverageDescription:
    "supplementaryInsuranceCoverageDescription",
  // The two grey plates between the photo cards. The abbreviation is the
  // Swiss-German one (KVG/VVG+) everywhere except FR, where Romandie uses
  // LAMal/LCA — the plates sat hardcoded in the markup until then, so the
  // French page rendered German law names next to French card titles.
  basicInsuranceCoverageBadge: "basicInsuranceCoverageBadge",
  supplementaryInsuranceCoverageBadge: "supplementaryInsuranceCoverageBadge",

  // Real-world examples
  examplesHeading: "examplesHeading",
  learnMoreAbout: "learnMoreAbout",
  examplesCoverageHeading: "examplesCoverageHeading",
  // Two variants of every insurer card, deliberately:
  //
  //   *Name / *Description        — the shared marketing summary. Rendered by
  //                                 the `/go/insurancemri` and
  //                                 `/go/insuranceblood` ad LPs.
  //   *CoverageName / *CoverageDescription
  //                               — the precise reimbursement terms, rendered
  //                                 ONLY by /insurance (and its localized
  //                                 routes /versicherung, /assurance) via
  //                                 `<InsurerExamples coveragePageCopy />`.
  //
  // They are separate keys because the two surfaces are owned separately: the
  // coverage page states percentages, per-calendar-year caps and exclusions,
  // while the LPs carry looser copy. Editing one set must never silently
  // retitle or re-scope the other. The Coverage* keys quote CHF figures and are
  // CH-only, so they are absent from the EUR catalogs — see
  // CH_ONLY_INSURANCE_PAGE_KEYS in scripts/lint-mirror-locales.ts.
  cssName: "cssName",
  cssCoverageName: "cssCoverageName",
  cssDescription: "cssDescription",
  cssCoverageDescription: "cssCoverageDescription",
  kptName: "kptName",
  kptDescription: "kptDescription",
  kptCoverageDescription: "kptCoverageDescription",
  visanaName: "visanaName",
  visanaDescription: "visanaDescription",
  visanaCoverageDescription: "visanaCoverageDescription",
  atupriName: "atupriName",
  atupriDescription: "atupriDescription",
  atupriCoverageDescription: "atupriCoverageDescription",
  axaName: "axaName",
  axaCoverageName: "axaCoverageName",
  axaDescription: "axaDescription",
  axaCoverageDescription: "axaCoverageDescription",
  groupeMutuelName: "groupeMutuelName",
  groupeMutuelDescription: "groupeMutuelDescription",
  groupeMutuelCoverageDescription: "groupeMutuelCoverageDescription",
  swicaName: "swicaName",
  swicaDescription: "swicaDescription",
  swicaCoverageDescription: "swicaCoverageDescription",
  egkName: "egkName",
  egkDescription: "egkDescription",
  egkCoverageDescription: "egkCoverageDescription",
  differentPlanText: "differentPlanText",
  differentPlanQualify: "differentPlanQualify",

  // FAQ
  //
  // `faqHeading` is shared with both ad LPs; `faqCoverageHeading` is the
  // /insurance-only variant (see the insurance-types note above).
  faqHeading: "faqHeading",
  faqCoverageHeading: "faqCoverageHeading",
  faq1Question: "faq1Question",
  faq1Answer: "faq1Answer",
  faq2Question: "faq2Question",
  faq2Answer: "faq2Answer",
  faq3Question: "faq3Question",
  faq3Answer: "faq3Answer",
  faq4Question: "faq4Question",
  faq4Answer: "faq4Answer",
  faq5Question: "faq5Question",
  faq5Answer: "faq5Answer",
  readAllFaqs: "readAllFaqs",

  // Bottom CTA
  ctaHeading: "ctaHeading",
  ctaSubtitle: "ctaSubtitle",
  bookCheckup: "bookCheckup",
} as const
